import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { FiPackage, FiTruck, FiCheckCircle, FiXCircle, FiClock, FiEye, FiRefreshCw } from 'react-icons/fi';

const Orders = () => {
  const { orderId } = useParams();
  const { isAuthenticated } = useAuth();
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const fetchOrders = async () => {
      try {
        setLoading(true);
        setError(null);

        if (orderId) {
          // Fetch specific order
          const response = await api.auth.getOrder(orderId);
          if (response.data.success) {
            setSelectedOrder(response.data.order);
          } else {
            setError('Order not found');
          }
        } else {
          // Fetch all orders
          const response = await api.auth.getOrders();
          if (response.data.success) {
            setOrders(response.data.orders);
          } else {
            setError('Failed to fetch orders');
          }
        }
      } catch (err) {
        console.error('Error fetching orders:', err);
        setError(api.utils.handleError(err));
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [orderId, isAuthenticated]);

  const canCancelOrder = (status) =>
    ['placed', 'processing', 'shipped'].includes(status);

  const handleCancelOrder = async (orderId, status) => {
    const isShipped = status === 'shipped';
    const confirmMessage = isShipped
      ? 'This order already has a shipment. Cancelling will also cancel the Shiprocket pickup so the courier does not come. Continue?'
      : 'Are you sure you want to cancel this order?';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await api.auth.cancelOrder(orderId);
      if (response.data.success) {
        setOrders(prevOrders =>
          prevOrders.map(order =>
            order.order_id === orderId
              ? { ...order, order_status: 'cancelled' }
              : order
          )
        );
        if (selectedOrder && selectedOrder.order_id === orderId) {
          setSelectedOrder(prev => ({ ...prev, order_status: 'cancelled' }));
        }
      } else {
        alert(response.data.error || 'Failed to cancel order');
      }
    } catch (err) {
      console.error('Error cancelling order:', err);
      alert(api.utils.handleError(err));
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'placed':
        return <FiClock className="text-yellow-500" size={20} />;
      case 'processing':
        return <FiPackage className="text-blue-500" size={20} />;
      case 'shipped':
        return <FiTruck className="text-purple-500" size={20} />;
      case 'delivered':
        return <FiCheckCircle className="text-green-500" size={20} />;
      case 'cancelled':
        return <FiXCircle className="text-red-500" size={20} />;
      default:
        return <FiClock className="text-gray-500" size={20} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'placed':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'shipped':
        return 'bg-purple-100 text-purple-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Please login to view your orders</h2>
          <Link to="/login" className="btn-primary">
            Login
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Error</h2>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // If viewing a specific order
  if (orderId && selectedOrder) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <Link to="/orders" className="flex items-center text-gray-600 hover:text-black mb-4">
            <FiRefreshCw className="mr-2" />
            Back to Orders
          </Link>
          <h1 className="text-3xl font-bold">Order Details</h1>
        </div>

        <div className="card">
          <div className="p-6">
            {/* Order Header */}
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl font-semibold">Order #{selectedOrder.order_id}</h2>
                <p className="text-gray-600">
                  Placed on {selectedOrder.created_at ? new Date(selectedOrder.created_at).toLocaleDateString() : 'Unknown date'}
                </p>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(selectedOrder.order_status)}`}>
                {selectedOrder.order_status.charAt(0).toUpperCase() + selectedOrder.order_status.slice(1)}
              </div>
            </div>

            {/* Order Items */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Order Items</h3>
              <div className="space-y-4">
                {selectedOrder.items && selectedOrder.items.length > 0 ? (
                  selectedOrder.items.map((item, index) => (
                    <div key={index} className="flex items-center space-x-4 p-4 border rounded-lg">
                      <img
                        src={api.utils.getImageUrl(item.image, { width: 1200, height: 1500, fit: 'cover' })}
                        alt={item.name}
                        className="w-20 aspect-[4/5] object-cover object-center rounded-lg"
                        onError={(e) => {
                          e.target.src = 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=100&h=100&fit=crop';
                        }}
                      />
                      <div className="flex-1">
                        <h4 className="font-medium">{item.name}</h4>
                        <p className="text-gray-600">Size: {item.size}</p>
                        <p className="text-gray-600">Quantity: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">₹{(item.price * item.quantity).toLocaleString()}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="text-gray-600">No items found for this order.</p>
                )}
              </div>
            </div>

            {/* Shipping Address */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Shipping Address</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                {selectedOrder.shipping_address ? (
                  <>
                    <p className="font-medium">{selectedOrder.shipping_address.fullName || 'N/A'}</p>
                    <p className="text-gray-600">{selectedOrder.shipping_address.address || 'N/A'}</p>
                    <p className="text-gray-600">
                      {selectedOrder.shipping_address.city || 'N/A'}, {selectedOrder.shipping_address.state || 'N/A'} {selectedOrder.shipping_address.pincode || 'N/A'}
                    </p>
                    <p className="text-gray-600">Phone: {selectedOrder.shipping_address.phone || 'N/A'}</p>
                  </>
                ) : (
                  <p className="text-gray-600">Shipping address not available</p>
                )}
              </div>
            </div>

            {/* Shipping / Tracking */}
            {selectedOrder.shipment?.awb_code && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-4">Shipment Tracking</h3>
                <div className="p-4 bg-purple-50 border border-purple-100 rounded-lg space-y-2">
                  <p className="text-sm"><span className="font-medium">AWB:</span> {selectedOrder.shipment.awb_code}</p>
                  {selectedOrder.shipment.courier_name && (
                    <p className="text-sm"><span className="font-medium">Courier:</span> {selectedOrder.shipment.courier_name}</p>
                  )}
                  {selectedOrder.shipment.tracking_url && (
                    <a
                      href={selectedOrder.shipment.tracking_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center text-sm text-purple-700 hover:text-purple-900 font-medium"
                    >
                      <FiTruck className="mr-1" size={16} />
                      Track your package
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Order Summary */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>₹{(selectedOrder.subtotal || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span>{(selectedOrder.shipping_cost || 0) === 0 ? 'Free' : `₹${selectedOrder.shipping_cost || 0}`}</span>
                </div>
                <div className="flex justify-between">
                  <span>Tax</span>
                  <span>₹{(selectedOrder.tax || 0).toLocaleString()}</span>
                </div>
                {(selectedOrder.discount || 0) > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount</span>
                    <span>-₹{(selectedOrder.discount || 0)}</span>
                  </div>
                )}
                <hr className="my-2" />
                <div className="flex justify-between text-lg font-semibold">
                  <span>Total</span>
                  <span>₹{(selectedOrder.total || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Actions */}
            {canCancelOrder(selectedOrder.order_status) && (
              <div className="flex space-x-4">
                <button
                  onClick={() => handleCancelOrder(selectedOrder.order_id, selectedOrder.order_status)}
                  className="btn-secondary"
                >
                  Cancel Order
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Orders list view
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold">My Orders</h1>
        <p className="text-gray-600">Track your orders and view order history</p>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <FiPackage className="text-gray-400" size={48} />
          </div>
          <h3 className="text-xl font-semibold mb-4">No Orders Yet</h3>
          <p className="text-gray-600 mb-8">Start shopping to see your orders here</p>
          <Link to="/products" className="btn-primary">
            Browse Products
          </Link>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order.order_id} className="card">
              <div className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      {getStatusIcon(order.order_status)}
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(order.order_status)}`}>
                        {order.order_status.charAt(0).toUpperCase() + order.order_status.slice(1)}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold">Order #{order.order_id}</h3>
                      <p className="text-sm text-gray-600">
                        Placed on {order.created_at ? new Date(order.created_at).toLocaleDateString() : 'Unknown date'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-bold">₹{(order.total || 0).toLocaleString()}</p>
                    <p className="text-sm text-gray-600">{order.items && order.items.length > 0 ? order.items.length : 0} items</p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <Link
                      to={`/orders/${order.order_id}`}
                      className="flex items-center space-x-2 text-blue-600 hover:text-blue-800"
                    >
                      <FiEye size={16} />
                      <span>View Details</span>
                    </Link>
                  </div>
                  
                  {canCancelOrder(order.order_status) && (
                    <button
                      onClick={() => handleCancelOrder(order.order_id, order.order_status)}
                      className="btn-secondary text-sm"
                    >
                      Cancel Order
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Orders;
